import { useMemo } from 'react';
import { Option } from '@common-fe/common-fe';

import {
  PaymentMethod,
} from '@/modules/transaction/components/TransactionDetails/TransactionPaymentDetails/constants';
import { useGetPaymentMethods } from '@/modules/transaction/components/TransactionDetails/TransactionPaymentDetails/useGetPaymentMethods.query';
import { PaymentMethodType } from '@/modules/user/user.types';

const PAYMENT_METHOD_LABELS: Record<PaymentMethodType, string> = {
  [PaymentMethodType.DIRECT_DEPOSIT]: 'Direct deposit',
  [PaymentMethodType.CHECK]: 'Check',
  [PaymentMethodType.PAYPAL]: 'PayPal',
  [PaymentMethodType.DEBIT]: 'Debit card',
  [PaymentMethodType.VENMO]: 'Venmo',
};

const getPaymentMethodLabel = (method: PaymentMethod): string => {
  const base = method.type ? PAYMENT_METHOD_LABELS[method.type] ?? method.type : '';
  const last4 = method.accountNumberLast4 || method.requisites?.cardLast4;
  return last4 ? `${base} ••${last4}` : base;
};

interface Props {
  employeeId?: string;
  excludeCheck?: boolean;
}

const useReturnedCheckPaymentMethodsQuery = ({ employeeId, excludeCheck }: Props) => {
  const { formattedData, isLoading } = useGetPaymentMethods(employeeId);

  const availableMethods = useMemo(
    () => (excludeCheck
      ? formattedData.filter((method) => method.type !== PaymentMethodType.CHECK)
      : formattedData),
    [formattedData, excludeCheck]
  );

  const onlyCheckLeft = useMemo(
    () => !!excludeCheck
      && formattedData.length > 0
      && formattedData.every((method) => method.type === PaymentMethodType.CHECK),
    [excludeCheck, formattedData]
  );

  const paymentMethodOptions = useMemo<Option[]>(
    () => availableMethods.map((method) => ({
      key: method.id,
      value: method.id,
      title: getPaymentMethodLabel(method),
    })),
    [availableMethods]
  );

  return {
    availableMethods,
    paymentMethodOptions,
    onlyCheckLeft,
    isLoading,
  };
};

export default useReturnedCheckPaymentMethodsQuery;
